import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { FiRefreshCw, FiRotateCw, FiSearch, FiAlertCircle } from 'react-icons/fi';
import { getAuth } from '../../utils/auth';
import { adminAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';

const ROLE_ALLOWLIST = ['system_admin', 'admin', 'manager'];

const statusBadgeClasses = {
  pending: 'bg-amber-100 text-amber-800',
  processed: 'bg-green-100 text-green-800',
  dead_letter: 'bg-red-100 text-red-800'
};

const prettyJson = (value) => JSON.stringify(value, null, 2);

const ZohoWebhookEvents = () => {
  const { userData: user } = getAuth();
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const hasAccess = user?.user_type === 'internal' && userRoles.some((role) => ROLE_ALLOWLIST.includes(role));

  const [loading, setLoading] = useState(false);
  const [replayingId, setReplayingId] = useState('');
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, processed: 0, dead_letter: 0, total: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getZohoWebhookEvents({
        page: currentPage,
        limit: 25,
        status: filters.status,
        search: filters.search
      });

      setEvents(response.data || []);
      setSummary(response.summary || { pending: 0, processed: 0, dead_letter: 0, total: 0 });
      setTotalPages(response.totalPages || 1);
      if (selectedEvent?.id) {
        const refreshed = await adminAPI.getZohoWebhookEvent(selectedEvent.id);
        if (refreshed) setSelectedEvent(refreshed);
      }
    } catch (error) {
      console.error('Error fetching Zoho webhook events:', error);
      setEvents([]);
      setSummary({ pending: 0, processed: 0, dead_letter: 0, total: 0 });
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasAccess) {
      fetchEvents();
    }
  }, [currentPage, filters.status, filters.search, hasAccess]);

  const handleReplay = async (eventId) => {
    setReplayingId(eventId);
    try {
      await adminAPI.replayZohoWebhookEvent(eventId);
      await fetchEvents();
      const refreshed = await adminAPI.getZohoWebhookEvent(eventId);
      setSelectedEvent(refreshed);
    } catch (error) {
      console.error('Error replaying webhook event:', error);
    } finally {
      setReplayingId('');
    }
  };

  const handleSelectEvent = async (event) => {
    setSelectedEvent(event);
    try {
      const detail = await adminAPI.getZohoWebhookEvent(event.id);
      if (detail) {
        setSelectedEvent(detail);
      }
    } catch (error) {
      console.error('Error fetching webhook event detail:', error);
    }
  };

  const summaryCards = useMemo(() => ([
    { label: 'Pending', value: summary.pending, className: 'border-amber-200 bg-amber-50 text-amber-900' },
    { label: 'Processed', value: summary.processed, className: 'border-green-200 bg-green-50 text-green-900' },
    { label: 'Dead Letter', value: summary.dead_letter, className: 'border-red-200 bg-red-50 text-red-900' },
    { label: 'Total', value: summary.total, className: 'border-slate-200 bg-slate-50 text-slate-900' }
  ]), [summary]);

  if (!hasAccess) {
    return (
      <div className="py-6">
        <div className="mx-auto px-4 sm:px-6 md:px-8">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
            Access denied. Admin or manager privileges required.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="mx-auto px-4 sm:px-6 md:px-8 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Zoho Webhooks</h1>
            <p className="mt-1 text-sm text-gray-500">
              Inspect queued Zoho invoice webhook events, failures, and replay status.
            </p>
          </div>
          <button
            onClick={fetchEvents}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <FiRefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {summaryCards.map((card) => (
            <div key={card.label} className={`rounded-lg border p-4 ${card.className}`}>
              <div className="text-sm font-medium">{card.label}</div>
              <div className="mt-1 text-2xl font-semibold">{card.value}</div>
            </div>
          ))}
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <label htmlFor="zoho-webhook-search" className="mb-1 block text-sm font-medium text-gray-700">
                Search
              </label>
              <div className="relative">
                <FiSearch className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                <input
                  id="zoho-webhook-search"
                  type="text"
                  value={filters.search}
                  onChange={(e) => {
                    setCurrentPage(1);
                    setFilters((prev) => ({ ...prev, search: e.target.value }));
                  }}
                  placeholder="Event type, invoice id, dedupe key, or error text"
                  className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label htmlFor="zoho-webhook-status" className="mb-1 block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="zoho-webhook-status"
                value={filters.status}
                onChange={(e) => {
                  setCurrentPage(1);
                  setFilters((prev) => ({ ...prev, status: e.target.value }));
                }}
                className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="processed">Processed</option>
                <option value="dead_letter">Dead letter</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="overflow-hidden rounded-lg bg-white shadow">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <LoadingSpinner />
                <span className="ml-2 text-gray-600">Loading webhook events...</span>
              </div>
            ) : events.length === 0 ? (
              <div className="py-16 text-center text-sm text-gray-500">No webhook events found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Event</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Resource</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Attempts</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {events.map((event) => (
                      <tr
                        key={event.id}
                        className={`cursor-pointer hover:bg-gray-50 ${selectedEvent?.id === event.id ? 'bg-blue-50' : ''}`}
                        onClick={() => handleSelectEvent(event)}
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                          {event.created_at ? format(new Date(event.created_at), 'MMM d, yyyy HH:mm') : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{event.event_type || 'Unknown event'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div>{event.resource_type || 'Unknown type'}</div>
                          <div className="text-xs text-gray-500">{event.resource_id || 'No resource id'}</div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClasses[event.status] || 'bg-gray-100 text-gray-800'}`}>
                            {event.status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{event.attempt_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="text-lg font-medium text-gray-900">Event Detail</h2>
            {!selectedEvent ? (
              <div className="mt-6 flex items-start rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                <FiAlertCircle className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0" />
                Select a webhook event to inspect its payload and last error.
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-1 gap-3 text-sm">
                  <div><span className="font-medium text-gray-700">Status:</span> {selectedEvent.status}</div>
                  <div><span className="font-medium text-gray-700">Event:</span> {selectedEvent.event_type || 'N/A'}</div>
                  <div><span className="font-medium text-gray-700">Resource:</span> {selectedEvent.resource_type || 'N/A'} {selectedEvent.resource_id ? `(${selectedEvent.resource_id})` : ''}</div>
                  <div><span className="font-medium text-gray-700">Attempts:</span> {selectedEvent.attempt_count}</div>
                  <div><span className="font-medium text-gray-700">Next attempt:</span> {selectedEvent.next_attempt_at ? format(new Date(selectedEvent.next_attempt_at), 'MMM d, yyyy HH:mm:ss') : 'N/A'}</div>
                  <div><span className="font-medium text-gray-700">Processed:</span> {selectedEvent.processed_at ? format(new Date(selectedEvent.processed_at), 'MMM d, yyyy HH:mm:ss') : 'Not processed'}</div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleReplay(selectedEvent.id)}
                    disabled={replayingId === selectedEvent.id}
                    className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FiRotateCw className="mr-1.5 h-3.5 w-3.5" />
                    {replayingId === selectedEvent.id ? 'Replaying...' : 'Replay Event'}
                  </button>
                </div>

                {selectedEvent.last_error && (
                  <div>
                    <h3 className="mb-2 text-sm font-medium text-gray-900">Last Error</h3>
                    <pre className="max-h-40 overflow-auto rounded-md bg-red-50 p-3 text-xs text-red-800 whitespace-pre-wrap">
                      {selectedEvent.last_error}
                    </pre>
                  </div>
                )}

                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-900">Payload</h3>
                  <pre className="max-h-[28rem] overflow-auto rounded-md bg-gray-900 p-3 text-xs text-gray-100">
                    {prettyJson(selectedEvent.payload || {})}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between rounded-lg bg-white px-6 py-4 shadow">
            <div className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZohoWebhookEvents;
